import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom';

import React, { useState, useEffect } from 'react';
import { AuthProvider } from './context/AuthContext';
import { useImageProtection } from './utils/imageProtection';
import { CartProvider } from './context/CartContext';

import PublicRoute from './components/PublicRoute';
import ProtectedRoute from './components/ProtectedRoute';

import './App.css';
import MainLayout from './layouts/MainLayout';

// Existing Pages
import LandingPage from './pages/LandingPage';
import Login from './pages/Login';
import Register from './pages/Register';
import VerifyEmail from './pages/VerifyEmail';
import Dashboard from './pages/Dashboard';
import ProfileUpload from './pages/ProfileUpload';
import MyPhotos from './pages/MyPhotos';
import PhotographerDashboard from './pages/PhotographerDashboard';
import ManagerRoster from './pages/ManagerRoster';
import CreditedPhotos from './pages/CreditedPhotos';
import CreateEvent from './pages/CreateEvent';
import EventPhotos from './pages/EventPhotos';
import EventAccess from './pages/EventAccess';
import EventQRCode from './pages/EventQRCode';
import Analytics from './pages/Analytics';
import AccountSettings from './pages/AccountSettings';
import Pricing from './pages/Pricing';
import EventBrowse from './pages/EventBrowse';
import EditEvent from './pages/EditEvent';
import PaymentHistory from './pages/PaymentHistory';
import Checkout from './pages/Checkout';
import CheckoutResult from './pages/CheckoutResult';
import SubscriptionResult from './pages/SubscriptionResult';
import NotFound from './pages/NotFound';
import FooterPage from './pages/FooterPage';

// V2 New Pages
import SlideshowViewer from './pages/SlideshowViewer';
import PhotographerProfile from './pages/PhotographerProfile';
import ContactPage from './pages/ContactPage';
// import DemoPage from './pages/DemoPage';
// import AdminDashboard from './pages/AdminDashboard';
// import GalleryAccess from './pages/GalleryAccess';
// import CheckoutPage from './pages/CheckoutPage';


import PublicEventsPage from './pages/PublicEventsPage';

const INTRO_LOADER_SESSION_KEY = 'photomatch_intro_loader_seen';
const INTRO_LOADER_DURATION_MS = 1800;

function PhotoMatchIntroLoader() {
  return (
    <div className="intro-loader" role="status" aria-live="polite" aria-busy="true">
      <div className="intro-loader-topbar">
        <div className="intro-loader-brand" aria-label="PhotoMatch">
          <span className="intro-loader-brand-mark">P</span>
          <span>PhotoMatch</span>
        </div>
      </div>

      <div className="intro-loader-stage">
        <div className="camera-loader" aria-hidden="true">
          <span className="camera-loader-flash" />
          <span className="camera-loader-body" />
          <span className="camera-loader-line camera-loader-line-top" />
          <span className="camera-loader-line camera-loader-line-bottom" />
          <span className="camera-loader-lens">
            <span className="camera-loader-ring" />
            <span className="camera-loader-core" />
          </span>
        </div>
        <p className="intro-loader-text">Loading</p>
      </div>
    </div>
  );
}



function App() {
  useImageProtection();

  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [showIntroLoader, setShowIntroLoader] = useState(() => {
    try {
      return sessionStorage.getItem(INTRO_LOADER_SESSION_KEY) !== 'true';
    } catch (error) {
      return true;
    }
  });

  useEffect(() => {
    const token = localStorage.getItem('token');
    const userData = localStorage.getItem('user');
    
    if (token && userData) {
      try {
        setUser(JSON.parse(userData));
      } catch (error) {
        localStorage.removeItem('user');
      }
    }
    setLoading(false);
  }, []);

  useEffect(() => {
    if (!showIntroLoader) {
      return undefined;
    }

    const timer = window.setTimeout(() => {
      try {
        sessionStorage.setItem(INTRO_LOADER_SESSION_KEY, 'true');
      } catch (error) {
        // Session storage can be unavailable in private browsing.
      }

      setShowIntroLoader(false);
    }, INTRO_LOADER_DURATION_MS);

    return () => window.clearTimeout(timer);
  }, [showIntroLoader]);

  if (loading || showIntroLoader) {
    return <PhotoMatchIntroLoader />;
  }

  return (

    <AuthProvider>
      <CartProvider>
      <BrowserRouter>
      
    
      <div className="App">
        {/*}
        <Navbar user={user} logout={logout} />
        <ToastContainer position="top-right" autoClose={3000} />
        */}
        
        <Routes>
          {/* Public Routes */}
          <Route element={<MainLayout />}>  
          <Route path="/" element={<LandingPage user={user} />} />
          <Route path="/pricing" element={<Pricing user={user} />} />
          <Route path="/about" element={<FooterPage type="about" />} />
          <Route path="/contact" element={<ContactPage />} />
          <Route path="/privacy" element={<FooterPage type="privacy" />} />
          <Route path="/terms" element={<FooterPage type="terms" />} />
          {/* <Route path="/demo" element={<DemoPage />} /> */}
          
          {/* Auth Routes 
          <Route 
            path="/login" 
            element={user ? <Navigate to="/dashboard" /> : <Login login={login} />} 
          />

          
          */}
          
          <Route path="/register"  element={<PublicRoute>  <Register /> </PublicRoute> }           />
          <Route path="/verify-email" element={<VerifyEmail />} />
          <Route path="/login" element={<PublicRoute><Login /></PublicRoute>} />
          <Route path="/dashboard" element={<ProtectedRoute><Dashboard /></ProtectedRoute>} />
          <Route path="/profile" element={<Navigate to="/account?tab=profile" replace />} />
          <Route path="/settings" element={<Navigate to="/account?tab=security" replace />} />


          {/* User Routes (Protected) 
          <Route 
            path="/dashboard" 
            element={user ? <Dashboard user={user} /> : <Navigate to="/login" />} 
          />

          */}

          <Route 
            path="/profile-upload" element={<ProtectedRoute> <ProfileUpload/> </ProtectedRoute>}
          />
          <Route 
            path="/my-photos" 
            element={<ProtectedRoute> <MyPhotos /> </ProtectedRoute>}
          />
          <Route 
            path="/account" 
            element={<ProtectedRoute> <AccountSettings /> </ProtectedRoute>}
          />
          <Route 
            path="/analytics" 
            element={<ProtectedRoute> <Analytics /> </ProtectedRoute>}
          />
          <Route
            path="/payment-history"
            element={<ProtectedRoute> <PaymentHistory /> </ProtectedRoute>}
          />
          <Route
            path="/checkout"
            element={<ProtectedRoute> <Checkout /> </ProtectedRoute>}
          />
          <Route
            path="/checkout/result"
            element={<ProtectedRoute> <CheckoutResult /> </ProtectedRoute>}
          />
          <Route
            path="/subscription/result"
            element={<ProtectedRoute> <SubscriptionResult /> </ProtectedRoute>}
          />

          {/* Photographer Routes (Protected) */}
          <Route
            path="/photographer"
            element={<ProtectedRoute> <PhotographerDashboard/> </ProtectedRoute>}
          />

          {/* Event manager: the roster of photographers they may credit. */}
          <Route
            path="/manager/photographers"
            element={<ProtectedRoute requiredRole="event_manager"> <ManagerRoster/> </ProtectedRoute>}
          />

          {/* A photographer's own byline, including work inside managed events. */}
          <Route
            path="/my-credited-photos"
            element={<ProtectedRoute requiredRole={['photographer', 'event_manager']}> <CreditedPhotos/> </ProtectedRoute>}
          />
          <Route 
            path="/create-event" 
            element={<ProtectedRoute> <CreateEvent/> </ProtectedRoute>}
          />
          <Route 
            path="/event/:eventId/photos" 
            element={<ProtectedRoute> <EventPhotos/> </ProtectedRoute>}
          />
          <Route
            path="/event/:eventId/access"
            element={<EventAccess />}
          />
          <Route
            path="/event/:eventId/browse"
            element={<ProtectedRoute> <EventBrowse/> </ProtectedRoute>}
          />
          <Route
            path="/event/:eventId/qr"
            element={<ProtectedRoute requiredRole={['photographer', 'event_manager']}> <EventQRCode/> </ProtectedRoute>}
          />
          <Route
            path="/event/:eventId/edit"
            element={<ProtectedRoute> <EditEvent/> </ProtectedRoute>}
          />
          
          {/* V2 New Routes */}


          <Route path="/events" element={<PublicEventsPage />} />
          <Route path="/photographer/:photographerId" element={<PhotographerProfile />} />



          <Route 
            path="/gallery/:galleryId/slideshow" 
            element={<SlideshowViewer />} 
          />
          {/* 
          <Route 
            path="/gallery/:galleryId/access" 
            element={user ? <GalleryAccess user={user} /> : <Navigate to="/login" />} 
          />
          <Route 
            path="/checkout" 
            element={user ? <CheckoutPage user={user} /> : <Navigate to="/login" />} 
          />
          */}
          
          {/* Admin Routes (Protected) */}
          {/* 
          <Route 
            path="/admin" 
            element={user?.is_admin ? <AdminDashboard user={user} /> : <Navigate to="/dashboard" />} 
          />
          */}
          <Route path="*" element={<NotFound />} />
          </Route>
        </Routes>
      </div>
    

     </BrowserRouter>
      </CartProvider>
    </AuthProvider>
  );
}

export default App;
